import { rem } from 'polished'
import React, { MouseEventHandler, useCallback, useContext } from 'react'
import styled from 'styled-components'

import BookingDetailsButtonAndTagView from './Common/BookingDetailsButtonAndTagView'
import ItemMenuOptions from './Common/ItemMenuOptions'
import TopContent from './Common/TopContent'
import { DesktopLayoutSizes } from './Common/utils'
import { ItemCardVariant } from './ItemCard'
import GooglePlaceRating from '../GooglePlaceRating/GooglePlaceRating'
import { AddRemoveItineraryButton } from '../Itinerary/AddRemoveItineraryButton'
import ItineraryDateButton from '../Itinerary/ItineraryDateButton'
import EditTripItem from '../TripModal/TripItems/Edit'
import AddedUpdatedBy from '../TripModal/TripItems/Summary/Common/AddedUpdatedBy'
import EditTripDates from '../TripModal/Trips/EditTripDatesTP'
import HeadingFullPrint from '../util/HeadingFullPrint'

import OnlyIfChildren from 'components/Common/OnlyIfChildren'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineMapMarkerIcon from 'components/Luxkit/Icons/line/LineMapMarkerIcon'
import LineNotesIcon from 'components/Luxkit/Icons/line/LineNotesIcon'
import StructureLabel from 'components/Luxkit/Label/StructureLabel'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import TextLink from 'components/Luxkit/TextLink'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import NoPrint from 'components/utils/NoPrint'
import PrintOnly from 'components/utils/PrintOnly'
import { useAppDispatch } from 'hooks/reduxHooks'
import { EmptyArray, nonNullable } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'
import { memoWithNestedProps } from 'lib/memoize/memoWithNestedProps'
import { SAVE_CURATED_TRIP_BUTTON_CLASS } from 'tripPlanner/components/Buttons/SaveCuratedTripItemButton'
import SaveTripItemButton from 'tripPlanner/components/Buttons/SaveTripItemButton'
import BaseItemCard from 'tripPlanner/components/ItemCard/BaseItemCard'
import ContentWrapper from 'tripPlanner/components/ItemCard/Common/ContentWrapper'
import { ItemImageContainer } from 'tripPlanner/components/ItemCard/ItemImageContainer'
import TripContext from 'tripPlanner/contexts/TripContext'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { ItemMetadata } from 'tripPlanner/models'
import { setCurrentSectionId } from 'tripPlanner/reducers/actions'
import MenuButton from 'tripPlanner/Tripkit/Core/MenuButton/MenuButton'
import { TripItem } from 'tripPlanner/types/tripItem'
import { isCustomItem, isLeBooking } from 'tripPlanner/utils'
import { itemSectionId } from 'tripPlanner/utils/itemSection'

const NonDesktopLayoutActionContainer = styled(Group)`
  ${DesktopLayoutSizes} {
    display: none;
  }
`

const PaddedBox = styled.div`
  ${mediaQueryUp.tablet} {
    padding: ${rem(12)};
  }
`

interface Props {
  item: TripItem
  /*
    Different item types have different logic for what exactly is shown for each of these fields.
    Hence they are passed in as props rather than extracted from the item within this component.
  */
  name: string
  /** URL of external image */
  imgSrc?: string
  /** ID of image from svc-image */
  imgId?: string
  placeId?: string
  locationDescription?: React.ReactNode
  descriptors?: Array<string | undefined>
  /** Alternate `descriptors` array to use for print view */
  printDescriptors?: Array<string | undefined>
  address?: string
  confirmationCode?: string
  confirmationCodeLabel?: string
  imageOverlayContent?: React.ReactNode
  bottomContent?: React.ReactNode
  /** Disables click handler, link and menu */
  nonInteractable?: boolean
  onClick?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>
  variant: ItemCardVariant
  onClickTripName?: () => void
  isPlaceRatingsVisible?: boolean
  amenitiesContent?: React.ReactNode
}

function ItineraryItemCard(props: Props) {
  const {
    item,
    name,
    imgSrc,
    imgId,
    placeId,
    locationDescription,
    descriptors = EmptyArray,
    printDescriptors = EmptyArray,
    address,
    confirmationCode,
    confirmationCodeLabel,
    imageOverlayContent,
    bottomContent,
    nonInteractable = false,
    onClick = noop,
    variant,
    onClickTripName = noop,
    isPlaceRatingsVisible = true,
    amenitiesContent,
  } = props
  const { type, createdAt, createdBy, userUpdatedAt, updatedBy, tripId } = item
  const tripContext = useContext(TripContext)
  const canEdit = tripContext?.permissions.canEdit ?? false
  const isPublicTrip = tripContext?.isPublicTrip ?? false
  const descriptorsString = nonNullable(descriptors)
    .filter((text) => text.length > 0)
    .join(' · ')
  const printDescriptorsString = nonNullable(printDescriptors)
    .filter((text) => text.length > 0)
    .join(' · ')
  // If we have different descriptors for print view, only show the normal ones in non-print view
  const DescriptorsWrapper = printDescriptorsString ? NoPrint : React.Fragment

  const dispatch = useAppDispatch()
  const onClickShowOnMap = useCallback<MouseEventHandler>(
    (e) => {
      e.preventDefault()
      dispatch(setCurrentSectionId(itemSectionId(item.id)))
    },
    [dispatch, item],
  )

  const openModal = useOpenModal()

  const onClickAddTripItemDates = useCallback(
    () => openModal(EditTripItem, { item }),
    [item, openModal],
  )

  const onClickTripNameLink = useCallback<MouseEventHandler>(
    (e) => {
      e.stopPropagation()
      onClickTripName()
    },
    [onClickTripName],
  )

  const onRequestAddTripDates = useCallback(
    (
      onDatesAdded?: (startDate: moment.Moment, endDate: moment.Moment) => void,
    ) => {
      openModal(EditTripDates, { tripId, onDatesAdded })
    },
    [openModal, tripId],
  )

  return (
    <BaseItemCard
      item={item}
      nonInteractable={nonInteractable}
      onClick={onClick}
    >
      <ItemImageContainer
        item={item}
        name={name}
        imgSrc={imgSrc}
        imgId={imgId}
        placeId={placeId}
        imageOverlayContent={
          <Group
            direction="horizontal"
            gap={8}
            horizontalAlign={imageOverlayContent ? 'space-between' : 'end'}
          >
            <div>{imageOverlayContent}</div>
            {!nonInteractable && canEdit && (
              <NonDesktopLayoutActionContainer
                direction="horizontal"
                gap={8}
                verticalAlign="center"
              >
                {isCustomItem(item) && (
                  <ItineraryDateButton
                    item={item}
                    variant="chip"
                    onRequestAddTripDates={onRequestAddTripDates}
                  />
                )}
                {!isCustomItem(item) && (
                  <AddRemoveItineraryButton
                    item={item}
                    onClickAddTripItemDates={onClickAddTripItemDates}
                  />
                )}
                <MenuButton
                  title=""
                  kind="secondary"
                  variant="dark"
                  shape="circle"
                  bodyContent={<ItemMenuOptions item={item} />}
                  footerContent={
                    !isPublicTrip &&
                    userUpdatedAt && (
                      <PaddedBox>
                        <AddedUpdatedBy
                          tripId={tripId}
                          createdAt={createdAt}
                          createdBy={createdBy}
                          updatedAt={userUpdatedAt}
                          updatedBy={updatedBy}
                          avatarSize="small"
                        />
                      </PaddedBox>
                    )
                  }
                />
              </NonDesktopLayoutActionContainer>
            )}
          </Group>
        }
      />
      <ContentWrapper>
        <TopContent
          item={item}
          nonInteractable={nonInteractable}
          variant={variant}
        >
          {ItemMetadata[type].singularName}
        </TopContent>

        <VerticalSpacer gap={12}>
          <VerticalSpacer gap={4}>
            <Group direction="horizontal" gap={4} verticalAlign="center">
              {locationDescription && (
                <CaptionTextBlock variant="large" lineClamp={2}>
                  {locationDescription}
                </CaptionTextBlock>
              )}
              {variant === 'tripSummary' && (
                <CSSBreakpoint min="desktop">
                  <Group direction="horizontal" gap={4} verticalAlign="center">
                    <CaptionTextBlock variant="large" colour="neutral-three">
                      ·
                    </CaptionTextBlock>
                    <TextLink
                      size="caption"
                      onClick={onClickShowOnMap}
                      weight="bold"
                    >
                      Show on map
                    </TextLink>
                  </Group>
                </CSSBreakpoint>
              )}
            </Group>
            <HeadingFullPrint variant="heading6" lineClamp={2}>
              {name}
            </HeadingFullPrint>
          </VerticalSpacer>
          {placeId && isPlaceRatingsVisible && (
            <GooglePlaceRating placeId={placeId} size="S" />
          )}
          <OnlyIfChildren as={VerticalSpacer} gap={0}>
            {confirmationCode && (
              <PrintOnly>
                <BodyText variant="medium">
                  {confirmationCodeLabel && (
                    <BodyText as="span" variant="medium" weight="bold">
                      {confirmationCodeLabel}:{' '}
                    </BodyText>
                  )}
                  {confirmationCode}
                </BodyText>
              </PrintOnly>
            )}
            <OnlyIfChildren as={VerticalSpacer} gap={12}>
              {amenitiesContent}
              {descriptorsString && (
                <DescriptorsWrapper>
                  <BodyTextBlock
                    variant="medium"
                    weight="normal"
                    startIcon={<LineNotesIcon size="S" />}
                  >
                    {descriptorsString}
                  </BodyTextBlock>
                </DescriptorsWrapper>
              )}
              {printDescriptorsString && (
                <PrintOnly>
                  <BodyText variant="medium">{printDescriptorsString}</BodyText>
                </PrintOnly>
              )}
            </OnlyIfChildren>
          </OnlyIfChildren>
        </VerticalSpacer>
        <OnlyIfChildren as={VerticalSpacer} gap={16}>
          {variant === 'drawer' && tripContext && (
            <div>
              <StructureLabel variant="information">
                <BodyTextBlock variant="small">Added to </BodyTextBlock>
                <TextLink size="caption" onClick={onClickTripNameLink}>
                  {tripContext.name}
                </TextLink>
              </StructureLabel>
            </div>
          )}
          {isLeBooking(item) && (
            <BookingDetailsButtonAndTagView
              onClick={onClick}
              variant={variant}
            />
          )}
          {bottomContent}
        </OnlyIfChildren>
        <SaveTripItemButton
          className={SAVE_CURATED_TRIP_BUTTON_CLASS}
          item={item}
        />
        {address && (
          <PrintOnly>
            <BodyTextBlock variant="small" startIcon={<LineMapMarkerIcon />}>
              {address}
            </BodyTextBlock>
          </PrintOnly>
        )}
        {item.notes && (
          <PrintOnly>
            <BodyText variant="small" weight="normal" wrap="pre-wrap">
              {item.notes}
            </BodyText>
          </PrintOnly>
        )}
      </ContentWrapper>
    </BaseItemCard>
  )
}

export default memoWithNestedProps(ItineraryItemCard, [
  'descriptors',
  'printDescriptors',
])
